/**
 * Family registration API – status, get (pre-fill), post (submit).
 * Admin: list by school year, get one for PDF view/export (with audit).
 * All calls require Authorization. Used when VITE_API_BASE_URL is set.
 */

import type {
  GetFamilyRegistrationStatusResponse,
  GetFamilyRegistrationChurchesResponse,
  GetFamilyRegistrationResponse,
  PostFamilyRegistrationRequest,
  ListFamilyRegistrationsResponse,
} from '@lambda/shared/types';
import { apiRequest } from './apiClient';

export const familyRegistrationApi = {
  getStatus(accessToken: string) {
    return apiRequest<GetFamilyRegistrationStatusResponse>(
      '/family-registration/status',
      accessToken,
      { method: 'GET' }
    );
  },

  /** Distinct church names from past "other" registrations for dropdown. */
  getChurches(accessToken: string) {
    return apiRequest<GetFamilyRegistrationChurchesResponse>(
      '/family-registration/churches',
      accessToken,
      { method: 'GET' }
    );
  },

  getRegistration(accessToken: string) {
    return apiRequest<GetFamilyRegistrationResponse>(
      '/family-registration',
      accessToken,
      { method: 'GET' }
    );
  },

  postRegistration(accessToken: string, body: PostFamilyRegistrationRequest) {
    return apiRequest<{ message: string; schoolYear: string }>(
      '/family-registration',
      accessToken,
      { method: 'POST', payload: body }
    );
  },

  /**
   * Admin: list family registrations for a school year.
   * Pass `limit` + `page` for server-side pagination (response includes totals);
   * `payment`/`enrollment`/`search`/`userId` filter and `sortBy`/`sortOrder` apply across all pages.
   */
  listRegistrations(
    accessToken: string,
    params?: {
      schoolYear?: string;
      limit?: number;
      page?: number;
      payment?: 'paid' | 'unpaid';
      enrollment?: 'enrolled' | 'not-enrolled';
      search?: string;
      userId?: string;
      sortBy?: 'familyName' | 'parentName' | 'completedAt' | 'payment';
      sortOrder?: 'asc' | 'desc';
    }
  ) {
    const searchParams = new URLSearchParams();
    if (params?.schoolYear) searchParams.set('schoolYear', params.schoolYear);
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.payment) searchParams.set('payment', params.payment);
    if (params?.enrollment) searchParams.set('enrollment', params.enrollment);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    const qs = searchParams.toString();
    return apiRequest<ListFamilyRegistrationsResponse>(
      `/admin/family-registrations${qs ? `?${qs}` : ''}`,
      accessToken,
      { method: 'GET' }
    );
  },

  /** Admin: get one registration + children for PDF (view or export). Writes audit log. */
  getRegistrationAdmin(
    accessToken: string,
    userId: string,
    schoolYear: string,
    action: 'view' | 'export'
  ) {
    const qs = new URLSearchParams({ schoolYear, action });
    return apiRequest<GetFamilyRegistrationResponse>(
      `/admin/family-registrations/${encodeURIComponent(userId)}?${qs}`,
      accessToken,
      { method: 'GET' }
    );
  },

  /** Admin: mark a family registration as paid (cash). Writes audit log. */
  markPaid(
    accessToken: string,
    userId: string,
    payload: { schoolYear: string; paymentMethod: 'cash' | 'check'; checkNumber?: string }
  ) {
    return apiRequest<{ message: string; paidAt: number; paymentMethod: string; checkNumber?: string }>(
      `/admin/family-registrations/${encodeURIComponent(userId)}/payment`,
      accessToken,
      { method: 'PUT', payload }
    );
  },

  /** Admin: remove a family registration payment (undo mark paid). Notifies the parent. */
  clearPaid(accessToken: string, userId: string, payload: { schoolYear: string }) {
    return apiRequest<{ message: string }>(
      `/admin/family-registrations/${encodeURIComponent(userId)}/payment`,
      accessToken,
      { method: 'DELETE', payload }
    );
  },

  /** Admin: apply, change, or remove a yearly-fee discount amount. Does not mark paid. */
  setDiscount(
    accessToken: string,
    userId: string,
    payload: { schoolYear: string; applied: boolean; discountCents?: number }
  ) {
    return apiRequest<{
      message: string;
      feeDiscountApplied: boolean;
      feeDiscountCents?: number;
    }>(
      `/admin/family-registrations/${encodeURIComponent(userId)}/discount`,
      accessToken,
      { method: 'PATCH', payload }
    );
  },
};
